/**
 * Shared helpers for learner programme progress snapshots.
 * Intake My Learning Progress uses `studentProgressCalc`.
 * Funder dashboards still use `fetchLearnerIntakeStructurePreviewV2`.
 */
import { httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { functions } from "../../../firebase";
import { buildUnitId } from "./contentHelpers/contentFetch";
import { LEGACY_COLLECTION_UPDATED_EVENT } from "../../../shared/legacyData/legacyCallable";
import { affectsLegacyProgress } from "../utils/legacyProgressInvalidation";
import { currentProgressRead } from "../utils/currentProgressRead";

const SNAPSHOT_MS = 45000;
const SEQUENTIAL_UNIT_GAP_MS = 250;
/** Delay between applying each unit to the intake UI after a batch snapshot returns. */
export const SEQUENTIAL_UNIT_REVEAL_MS = 200;

/** Same intake-structure callable as the funder dashboard. */
export const INTAKE_STRUCTURE_PREVIEW_CALLABLE =
  "fetchLearnerIntakeStructurePreviewV2";

/** Dedicated learner intake progress calculator (outline + exact % + missing). */
export const STUDENT_PROGRESS_CALC_CALLABLE = "studentProgressCalc";

/** In-flight dedupe so dashboard preload and structure preview share one V2 call. */
const progressFetchInflight = new Map();
const progressIdentity = () => {
  try { return getAuth(functions.app).currentUser?.uid || "anonymous"; }
  catch { return "anonymous"; }
};
if (typeof window !== "undefined") {
  const invalidate = (event) => {
    if (affectsLegacyProgress(event)) progressFetchInflight.clear();
  };
  [LEGACY_COLLECTION_UPDATED_EVENT, "learner-quiz-completed", "learner-assessment-reviewed"]
    .forEach((event) => window.addEventListener(event, invalidate));
}

export const buildProgressRequestKey = (programId, learnerIds, unitIds) =>
  `${String(programId || "").trim()}|||${(learnerIds || [])
    .map((id) => String(id || "").trim())
    .filter(Boolean)
    .join("|")}|||${(unitIds || [])
    .map((id) => String(id || "").trim())
    .filter(Boolean)
    .join("||")}`;

export async function fetchProgramLearnerProgressUnitsMapDeduped(
  programIdForCf,
  learnerIds,
  unitIds,
  callableName = INTAKE_STRUCTURE_PREVIEW_CALLABLE,
) {
  const requestKey = `${progressIdentity()}|||${buildProgressRequestKey(
    programIdForCf,
    learnerIds,
    unitIds,
  )}|||${callableName}`;
  if (progressFetchInflight.has(requestKey)) {
    return progressFetchInflight.get(requestKey);
  }
  const timeoutMs =
    callableName === STUDENT_PROGRESS_CALC_CALLABLE ? 190000 : SNAPSHOT_MS;
  const promise = currentProgressRead(() => {
    const newer = progressFetchInflight.get(requestKey);
    if (newer && newer !== promise) return newer;
    return fetchProgramLearnerProgressUnitsMap(
    programIdForCf,
    learnerIds,
    unitIds,
    callableName,
    timeoutMs,
    );
  }, progressIdentity).finally(() => {
    if (progressFetchInflight.get(requestKey) === promise) progressFetchInflight.delete(requestKey);
  });
  progressFetchInflight.set(requestKey, promise);
  return promise;
}

const normalizeLooseKey = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** Same rules as UnitsView / IntakeCourseStructurePreview */
export const hasAssessmentAttempt = (entry = {}) => {
  if (!entry || typeof entry !== "object") return false;
  const score = Number(entry?.score);
  const hasScore = Number.isFinite(score) && score > 0;
  const hasResults = Array.isArray(entry?.results) && entry.results.length > 0;
  const hasResultsCount = Number(entry?.resultsCount || 0) > 0;
  const hasAnswers = Array.isArray(entry?.answers) && entry.answers.length > 0;
  const hasAnswersCount = Number(entry?.answersCount || 0) > 0;
  const hasQuestions =
    entry?.questions && Object.keys(entry.questions || {}).length > 0;
  return (
    Boolean(entry?.completed) ||
    Boolean(entry?.completedAt) ||
    hasScore ||
    hasResults ||
    hasResultsCount ||
    hasAnswers ||
    hasAnswersCount ||
    hasQuestions
  );
};

export function statsFromSnapshotNode(node) {
  const p = node?.progress;
  if (p && typeof p === "object") {
    const total = Number(p.totalAssessments) || 0;
    const completed = Number(p.completedAssessments) || 0;
    if (total > 0) {
      return {
        total,
        completed: Math.min(completed, total),
        percentHint: null,
        missingAssessments:
          Number(p.missingAssessments) >= 0
            ? Number(p.missingAssessments)
            : Math.max(0, total - Math.min(completed, total)),
      };
    }
    if (typeof p.percent === "number" && !Number.isNaN(p.percent)) {
      return { total: 0, completed: 0, percentHint: p.percent };
    }
  }
  const outcomes = node?.outcomes;
  if (outcomes && typeof outcomes === "object") {
    let total = 0;
    let completed = 0;
    Object.values(outcomes).forEach((items) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        if (!item?.hasAssessment) return;
        total += 1;
        if (hasAssessmentAttempt(item?.attempt)) completed += 1;
      });
    });
    if (total > 0) {
      return { total, completed, percentHint: null };
    }
  }
  const summary = node?.summary || {};
  const summaryEntries = Object.values(summary).filter(Boolean);
  if (summaryEntries.length > 0) {
    const tot = summaryEntries.length;
    const done = summaryEntries.filter((e) => hasAssessmentAttempt(e)).length;
    return { total: tot, completed: done, percentHint: null };
  }
  return null;
}

export function findSnapshotUnitNode(unitsPayload, unitOrName, programId = "") {
  if (!unitsPayload || !unitOrName) return null;
  const unitName =
    typeof unitOrName === "string"
      ? String(unitOrName).trim()
      : String(unitOrName?.name || "").trim();
  if (!unitName && typeof unitOrName !== "object") return null;

  const unitId =
    typeof unitOrName === "object" ? String(unitOrName?.unitId || "").trim() : "";
  const slugId =
    unitName && programId
      ? buildUnitId(unitName, programId, "TPC_Group")
      : "";
  const target = normalizeLooseKey(unitName);
  const idKeys = new Set(
    [unitId, slugId].map((k) => String(k || "").trim()).filter(Boolean),
  );

  const nodeMatches = (node, payloadKey = "") => {
    if (!node || typeof node !== "object") return false;
    const n = String(node?.unitName || node?.name || "").trim();
    if (n && normalizeLooseKey(n) === target) return true;
    const nid = String(node?.unitId || "").trim();
    if (nid && idKeys.has(nid)) return true;
    const pk = String(payloadKey || "").trim();
    if (pk && idKeys.has(pk)) return true;
    if (pk && normalizeLooseKey(pk) === target) return true;
    return false;
  };

  for (const [payloadKey, node] of Object.entries(unitsPayload)) {
    if (nodeMatches(node, payloadKey)) return node;
  }
  for (const node of Object.values(unitsPayload)) {
    if (nodeMatches(node)) return node;
  }
  return null;
}

export function buildUnitStatsFromSnapshotPayload(
  unitsPayload,
  unitList,
  programId = "",
) {
  const out = {};
  if (!unitsPayload || typeof unitsPayload !== "object") return out;
  for (const u of unitList) {
    const un = u?.name;
    if (!un) continue;
    const node = findSnapshotUnitNode(unitsPayload, u, programId);
    const st = node ? statsFromSnapshotNode(node) : null;
    if (st) out[un] = st;
  }
  return out;
}

/** Ignore empty snapshot rows so we refetch instead of sticking at 0%. */
export function unitStatsAreUsable(stats) {
  if (!stats) return false;
  if (stats.total > 0) return true;
  if (
    typeof stats.percentHint === "number" &&
    !Number.isNaN(stats.percentHint) &&
    stats.percentHint > 0
  ) {
    return true;
  }
  return false;
}

/** Same unwrap as IntakeCourseStructurePreview */
export function extractLearningProgressUnitsPayload(res) {
  const d = res?.data;
  if (!d || typeof d !== "object") return {};
  if (d.data && typeof d.data === "object" && d.data.units && typeof d.data.units === "object") {
    return d.data.units;
  }
  if (
    d.success &&
    d.data &&
    typeof d.data === "object" &&
    d.data.units &&
    typeof d.data.units === "object"
  ) {
    return d.data.units;
  }
  if (d.units && typeof d.units === "object") return d.units;
  if (d.result?.data?.units && typeof d.result.data.units === "object") {
    return d.result.data.units;
  }
  if (d.result?.units && typeof d.result.units === "object") {
    return d.result.units;
  }
  return {};
}

/**
 * One call: load learner history once, then units in programme order with
 * exact progress and missing-assessment counts.
 */
export async function fetchStudentProgressCalc(
  programIdForCf,
  learnerIds,
  unitIds = [],
  timeoutMs = 190000,
) {
  const key = `${progressIdentity()}|||student|||${buildProgressRequestKey(programIdForCf, learnerIds, unitIds)}|||${timeoutMs}`;
  if (progressFetchInflight.has(key)) return progressFetchInflight.get(key);
  const promise = currentProgressRead(
    () => {
      const newer = progressFetchInflight.get(key);
      return newer && newer !== promise ? newer :
        fetchStudentProgressCalcFresh(programIdForCf, learnerIds, unitIds, timeoutMs);
    }, progressIdentity)
    .finally(() => {
      if (progressFetchInflight.get(key) === promise) progressFetchInflight.delete(key);
    });
  progressFetchInflight.set(key, promise);
  return promise;
}

async function fetchStudentProgressCalcFresh(
  programIdForCf,
  learnerIds,
  unitIds = [],
  timeoutMs = 190000,
) {
  const ids = (learnerIds || [])
    .map((id) => String(id || "").trim())
    .filter(Boolean);
  const emailId = ids.find((id) => id.includes("@")) || "";
  const primaryLearner = emailId || ids[0] || "";
  if (!primaryLearner || !programIdForCf) {
    return { units: {}, unitOrder: [] };
  }
  const orderedIds = emailId
    ? [emailId, ...ids.filter((id) => id !== emailId)]
    : ids;
  let timer;
  try {
    const calcFn = httpsCallable(functions, STUDENT_PROGRESS_CALC_CALLABLE, {
      timeout: Math.max(timeoutMs, 190000),
    });
    const res = await Promise.race([
      calcFn({
        learnerUid: primaryLearner,
        learnerEmail: emailId || primaryLearner,
        learnerUids: orderedIds,
        company: "TPC_Group",
        collectionBrand: "TechPerformanceCentre",
        programId: programIdForCf,
        unitIds: Array.isArray(unitIds) ? unitIds : [],
      }),
      new Promise((_, reject) =>
        timer = setTimeout(() => reject(new Error("snapshot-timeout")), timeoutMs),
      ),
    ]);
    const d = res?.data;
    const inner = d?.data && typeof d.data === "object" ? d.data : d;
    const units =
      inner?.units && typeof inner.units === "object" ? inner.units : {};
    const unitOrder = Array.isArray(inner?.unitOrder) ? inner.unitOrder : [];
    if (Object.keys(units).length === 0) {
      console.warn("[studentProgressCalc] empty units payload", {
        programId: programIdForCf,
        keys: inner ? Object.keys(inner) : [],
      });
    }
    return {
      units,
      unitOrder,
      updatedAt: inner?.updatedAt || null,
    };
  } catch (error) {
    console.warn("[studentProgressCalc] failed", error?.code || "", error?.message || error);
    return { units: {}, unitOrder: [] };
  } finally {
    clearTimeout(timer);
  }
}

/** All unit document ids for the programme tree (for one bulk snapshot call). */
export function collectAllProgrammeUnitIds(programData, programIdForCf) {
  if (!programData?.modules || !programIdForCf) return [];
  const names = [];
  (programData.modules || []).forEach((m) => {
    (m.components || []).forEach((componentName) => {
      const comp = programData.components?.find((c) => c.name === componentName);
      (comp?.units || []).forEach((un) => {
        if (un) names.push(un);
      });
    });
  });
  return [...new Set(names)].map((unitName) => {
    const u = programData.units?.find((x) => x.name === unitName);
    return (
      String(u?.unitId || "").trim() ||
      buildUnitId(unitName, programIdForCf, "TPC_Group")
    );
  }).filter(Boolean);
}

/**
 * Returns merged `units` map (same shape as IntakeCourseStructurePreview cache).
 */
export async function fetchProgramLearnerProgressUnitsMap(
  programIdForCf,
  learnerIds,
  unitIds,
  callableName = INTAKE_STRUCTURE_PREVIEW_CALLABLE,
  timeoutMs = SNAPSHOT_MS,
) {
  const primaryLearner = String(learnerIds[0] || "").trim();
  if (!primaryLearner || !programIdForCf || !Array.isArray(unitIds) || unitIds.length === 0) {
    return {};
  }
  const basePayload = {
    learnerUid: primaryLearner,
    learnerUids: learnerIds,
    company: "TPC_Group",
    collectionBrand: "TechPerformanceCentre",
    programId: programIdForCf,
    unitIds,
  };
  const race = async (promise) => {
    let timer;
    try {
      return await Promise.race([
        promise,
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error("snapshot-timeout")), timeoutMs);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  };

  let mergedMap = {};
  try {
    const previewFn = httpsCallable(functions, callableName, {
      timeout: Math.max(timeoutMs, 120000),
    });
    mergedMap = extractLearningProgressUnitsPayload(await race(previewFn(basePayload)));
  } catch (error) {
    console.warn(`[learningProgressSnapshot] ${callableName} failed`, error?.message || error);
  }
  return mergedMap;
}

/**
 * Fetch intake structure + assessment attempts one unit at a time (V3).
 * Each chunk is applied immediately so progress/outlines appear in programme order.
 */
export async function fetchProgramLearnerProgressUnitsSequentially(
  programIdForCf,
  learnerIds,
  unitIdGroups,
  {
    callableName = INTAKE_STRUCTURE_PREVIEW_CALLABLE,
    gapMs = SEQUENTIAL_UNIT_GAP_MS,
    onChunk = null,
    isCancelled = () => false,
  } = {},
) {
  const groups = Array.isArray(unitIdGroups)
    ? unitIdGroups
        .map((group) =>
          (Array.isArray(group) ? group : [group])
            .map((id) => String(id || "").trim())
            .filter(Boolean),
        )
        .filter((group) => group.length > 0)
    : [];
  const merged = {};
  for (let index = 0; index < groups.length; index += 1) {
    if (isCancelled()) break;
    const unitIds = groups[index];
    const chunk = await fetchProgramLearnerProgressUnitsMap(
      programIdForCf,
      learnerIds,
      unitIds,
      callableName,
      60000,
    );
    Object.assign(merged, chunk);
    if (typeof onChunk === "function") {
      await onChunk(chunk, index, unitIds);
    }
    if (index < groups.length - 1 && gapMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, gapMs));
    }
  }
  return merged;
}

/** Extract one unit's nodes from a batch snapshot (handles name/id payload keys). */
export function buildSnapshotChunkForUnit(unitsPayload = {}, unitOrName, programId = "") {
  if (!unitsPayload || typeof unitsPayload !== "object") return null;
  const node = findSnapshotUnitNode(unitsPayload, unitOrName, programId);
  if (!node) return null;
  const chunk = {};
  Object.entries(unitsPayload).forEach(([key, value]) => {
    if (value === node) {
      chunk[key] = value;
      return;
    }
    if (findSnapshotUnitNode({ [key]: value }, unitOrName, programId)) {
      chunk[key] = value;
    }
  });
  const unitName =
    typeof unitOrName === "string"
      ? String(unitOrName).trim()
      : String(unitOrName?.name || node?.unitName || "").trim();
  if (unitName && !chunk[unitName]) {
    chunk[unitName] = node;
  }
  return Object.keys(chunk).length > 0 ? chunk : null;
}
